/**
 * Canonical number utility functions.
 * Extracted from inline implementations across ~17 source files.
 * No behavior change — pure consolidation.
 */

/**
 * Converts any input to a finite number. Returns 0 for non-finite, nullish, or unparseable.
 * @param {*} value
 * @returns {number}
 */
export function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Locale-aware number parsing. Strips non-numeric chars, replaces ',' with '.'.
 * Handles AR format: 1.234,56 → 1234.56. Returns 0 for nullish/unparseable.
 * @param {*} value
 * @returns {number}
 */
export function toNumberLocale(value) {
  if (value == null) return 0
  const cleaned = String(value)
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '')
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : 0
}

/**
 * Formats a number as currency using Intl.NumberFormat.
 * Default: 'es-AR' locale, 'ARS' currency. Returns '-' for invalid inputs.
 * @param {*} amount
 * @param {object} [options]
 * @param {string} [options.locale='es-AR']
 * @param {string} [options.currency='ARS']
 * @param {number} [options.minimumFractionDigits=2]
 * @param {number} [options.maximumFractionDigits=2]
 * @returns {string}
 */
export function toCurrency(amount, options = {}) {
  if (amount == null || (typeof amount !== 'number' && typeof amount !== 'string')) return '-'
  const n = Number(amount)
  if (!Number.isFinite(n)) return '-'
  const {
    locale = 'es-AR',
    currency = 'ARS',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(n)
}

/**
 * Constrains a value within [min, max] inclusive.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

/**
 * Computes signed percentage difference: ((current - previous) / previous) * 100.
 * Rounded to 2 decimal places. Returns 0 when previous is 0 or not finite.
 * @param {number} current
 * @param {number} previous
 * @returns {number}
 */
export function porcentualDiff(current, previous) {
  const p = Number(previous)
  const c = Number(current)
  if (!Number.isFinite(p) || !Number.isFinite(c) || p === 0) return 0
  return Number((((c - p) / p) * 100).toFixed(2))
}
